import { TimeOffsetUnit } from 'constants/enums';

import { UserOrganizationType } from './organization.types';
import { EditableInvites } from './invites.types';
import {
  PreEventQuestions,
  EditablePreEventQuestions,
  FeedbackQuestions,
  EditableFeedbackQuestions
} from './questions.types';
import { EventQuestion, GenericEventQueryQuestion } from './question.types';
import { GuestStats, GuestStatus, GuestsPlatformConfirmationsStats } from './guest.types';
import { PlusOneConfig } from './plus-one.types';
import { EventReminder } from './reminder.types';
import { CompanyTag } from './company.types';
import { EventOrganizer } from './event_organizers.types';

interface HostOrganizationType extends UserOrganizationType {
  aboutDescription: string;
}

export interface SelfCheckinSchedule {
  quantity: number;
  unit: TimeOffsetUnit;
  beforeEventBegins: boolean;
}

export interface Schedule {
  quantity?: number | string;
  unit?: string;
  beforeEventBegins?: boolean;
}
export interface Reminder {
  id?: number | undefined;
  uuid?: string;
  notifyStatus?: number;
  message?: string;
  title?: string;
  datetime?: string;
  flagDelete?: boolean;
  isCreated?: boolean;
  schedule?: Schedule;
  selectedGuestStatuses?: number[];
}

export interface EmailReminder {
  id?: number | undefined;
  subject: string;
  message: string;
  uuid?: string;
  notifyStatus?: number;
  datetime?: string;
  isCreated?: boolean;
  schedule?: Schedule;
  selectedGuestStatuses?: number[];
}

export interface EventAnnouncement {
  id: number;
  status: string;
  channel: string;
  confirmations: number;
  createdBy: {
    firstName: string;
    lastName: string;
  };
  slackChannels: string[];
  datetimeSend: string;
  timezoneCode: string;
  events: Event[];
}

interface EventAnnouncementWithConfirmations extends EventAnnouncement {
  confirmations: number;
}

export interface EventUser {
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

export enum EventStatus {
  'ACTIVE' = 'ACTIVE',
  'INACTIVE' = 'INACTIVE'
}

export interface CalendarDetails {
  creatorFirstName?: string;
  creatorLastName?: string;
  lastUpdatedAt?: string;
}

export interface Session extends Omit<Event, 'sessions'> {
  parentId?: number;
  guestListTotals?: Partial<GuestStats>;
  hasZoomMeeting?: boolean;
  hasGoogleMeeting?: boolean;
  feedbackEnable?: boolean;
  hasAttendedGuest?: boolean;
  hasUnlimitedSpots?: boolean;
  targetAttendance?: number;
  maxAttendees?: number;
  numSpotsLeft?: number;
  isEventWaitlisted?: boolean;
}
export interface EditableSession extends Omit<EditableEvent, 'sessions' | 'id'> {
  id?: number;
  parentId?: number;
  uuid?: string;
  guestListTotals?: Partial<GuestStats>;
  zoomMeetingId?: number;
  googleMeetingId?: number;
  isCreatedForFirstTime?: boolean;
  isSelfCheckinEditable?: boolean;
  hasUnlimitedSpots?: boolean;
  targetAttendance?: number;
  maxAttendees?: number;
}

export interface Event {
  id: number;
  photoUrl: string;
  originalPhotoUrl?: string;
  slackPhotoUrl?: string;
  photoXCrop?: number;
  photoYCrop?: number;
  photoZoom?: number;
  isThirtyDaysAfterEventEnded?: boolean;
  title: string;
  description?: string;
  eventType?: EventType;
  hostOrganizationName: string;
  createdBy?: EventUser;
  createdAt?: string;
  editedAt?: string;
  editedBy?: EventUser;
  archivedAt?: string;
  datetimeScheduled: string;
  datetimeScheduledChanged: boolean;
  datetimeEnd: string;
  timezoneCode?: string;
  locationAddress?: string;
  locationName: string;
  slackFormattedLocation?: string;
  hasStarted?: boolean;
  hasEnded: boolean;
  hasZoomMeeting?: boolean;
  hasGoogleMeeting?: boolean;
  isPrivate: boolean;
  isPastRegistrationCutoff: boolean;
  isPastQuestionnaireCutoff: boolean;
  hostOrganization: HostOrganizationType;
  allDay: boolean;
  isVirtual?: boolean;
  isInPerson?: boolean;
  isPublished: boolean;
  isArchived?: boolean;
  isFeedbackRequestNotificationsTracked: boolean;
  sentFeedbackRequestNotificationsCount: number;
  companyLocation?: {
    id: number;
    name: string;
  };
  virtualEventInfo?: {
    hasUnlimitedSpots: boolean;
    virtualLocation: string;
  };
  virtualLocationHost?: string;
  calendarNotifications: boolean;
  sendCalendarInvites?: boolean;
  transparency: CalendarTransparency;
  calendarDetails?: CalendarDetails;
  allowUserCheckin?: boolean;
  registrationCutoffAt?: string;
  questionnaireCutoffAt?: string;
  sessions?: Session[];
  isUsingEstimatedAttendees?: boolean;
  estimatedAttendees?: number;
  currentUserMatchStatus?: GuestStatus;
  tags?: string[];
  contactName?: string;
  contactEmail?: string;
  scheduledDeletionAt?: string;
  // @deprecated
  t2Domain?: T2DomainEnum;
  t2DomainArray?: T2DomainEnum[];
  plusOneConfig?: PlusOneConfig;
}

export interface PublishedEvent extends Event {
  isPublished: boolean;
  guestListTotals: Partial<GuestStats>;
}

export interface EventWithQuestions
  extends Event,
    PublishedEvent,
    PreEventQuestions,
    FeedbackQuestions {}

export interface ViewableEvent extends EventWithQuestions {
  numSpotsLeft: number;
  isClosed: boolean;
  canSeeEventGuestList: boolean;
  userInvited: boolean;
  guestListTotals: GuestStats;
  tags?: string[];
  contactName?: string;
  contactEmail?: string;
  limitRegistration?: boolean;
}

export enum EventType {
  'SINGLE' = 'SINGLE',
  'MULTI_SESSIONS' = 'MULTI_SESSIONS',
  'SESSION' = 'SESSION'
}

export enum CreateEventType {
  'SINGLE' = 'SINGLE',
  'MULTI_SESSIONS' = 'MULTI_SESSIONS',
  'AI_GENERATED' = 'AI_GENERATED'
}

export enum GuestListPrivacyEnum {
  VISIBLE = 'visible',
  HIDDEN = 'hidden'
}

export enum CalendarTransparency {
  'OPAQUE' = 0,
  'TRANSPARENT' = 1
}

type ZoomMeeting = {
  id: number;
};

type GoogleMeeting = {
  id: number;
};

export interface EditableEvent
  extends Partial<EventWithQuestions>,
    Partial<EditableInvites>,
    Partial<EditablePreEventQuestions>,
    Partial<EditableFeedbackQuestions> {
  companyId?: number;
  communityInviteeIds?: number[];
  preEventQuestionsToDelete?: Partial<EventQuestion>[];
  feedbackQuestionsToDelete?: Partial<EventQuestion>[];
  eventType?: EventType;
  status?: EventStatus;
  hostOrganizationId?: number;
  contactName?: string;
  contactEmail?: string;
  tags?: string[];
  virtualLocation?: string;
  virtualLocationHost?: string;
  companyLocationId?: number;
  physicalLocation?: string;
  hasUnlimitedSpots?: boolean;
  maxAttendees?: number;
  targetAttendance?: number | string | null;
  listPrivacy?: string;
  guestListPrivacy?: GuestListPrivacyEnum | null;
  createSlackChannel?: boolean;
  slackChannelId?: string;
  slackTeamId?: string;
  slackChannelName?: string;
  slackChannelDate?: string;
  slackChannelAutoarchive?: boolean;
  preEventUseTemplate?: boolean;
  feedbackUseTemplate?: boolean;
  feedbackSubmittedCount?: number;
  calendarNotifications?: boolean;
  slackNotifications?: boolean | null;
  slackReminders?: Reminder[];
  emailNotifications?: boolean | null;
  emailReminders?: EmailReminder[];
  hasZoomMeeting?: boolean;
  hasGoogleMeeting?: boolean;
  draftData?: string;
  isPublished?: boolean;
  isArchived?: boolean;
  sendCalendarInvites?: boolean;
  sendSlackInvites?: boolean;
  sendUserCheckinEmail?: boolean;
  sendUserCheckinSlack?: boolean;
  sendUserCheckinSchedule?: Schedule | null;
  selfCheckinSchedule?: SelfCheckinSchedule | null;
  isSelfCheckinEditable?: boolean;
  sessions?: Session[];
  hasZoomParticipants?: boolean;
  hasMeetParticipants?: boolean;
  hasRegistrationCutoffTime?: boolean;
  hasQuestionnaireCutoffTime?: boolean;
  hasDraftData?: boolean;
  budget?: number | null;
  hasOutlookCalendarEvent?: boolean;
  limitRegistration?: boolean;
  isEventSplitAcrossCalendarEvents?: boolean;
  // @deprecated
  t2Domain?: T2DomainEnum;
  t2DomainArray?: T2DomainEnum[];
  zoomMeeting?: ZoomMeeting;
  googleMeeting?: GoogleMeeting;
  categories?: CompanyTag[];
  plusOneConfig?: PlusOneConfig;
  organizer?: EventOrganizer;
  totalGuests?: number;
  canRestoreFromCancellation?: boolean;
  isGuestCheckinOpen?: boolean;
  totalCheckedInGuests?: number;
  totalDeactivatedGuests?: number;
  guestHasNoneStatus?: boolean;
  publishSchedule?: EventPublishSchedule | null;
}

export interface InsightsEvent extends Event {
  participantsTotal: number;
  surveySubmittedCount: number;
  feedbackSubmittedCount: number;
  feedbackEnable: boolean;
  hasZoomMeeting: boolean;
  preEventQuestions: GenericEventQueryQuestion[];
  preeventEnable: boolean;
  postQuestions: GenericEventQueryQuestion[];
  allReminders: EventReminder[];
  announcementsWithConfirmations: EventAnnouncementWithConfirmations[];
  distrolistInviteeIds?: number[];
  communityInviteeIds?: number[];
  isUsingEstimatedAttendees?: boolean;
  estimatedAttendees?: number;
  guestHasNoneStatus?: boolean;
  isFeedbackAnonymous?: boolean;
}

export interface UpcomingEvent extends PublishedEvent {
  thumbnailUrl?: string;
  slackPhotoUrl?: string;
  organization?: UserOrganizationType;
  canSeeEventGuestList?: boolean;
  confirmedUsersSample?: EventUser[];
  tags?: string[];
}

export interface InsightsEventQueryResponse {
  event: InsightsEvent;
  guestStats: GuestStats;
  guestsPlatformConfirmationsStats: GuestsPlatformConfirmationsStats;
}

export interface InsightsEventQueryVariables {
  id: number;
}

export interface EventChangesQueryResult {
  eventChanges: {
    areEligibleToSendNotifications: boolean;
    isStartDatetimeChanging: boolean;
    isEndDatetimeChanging: boolean;
    changedSessionsCount: number;
    isTitleChanging: boolean;
    isDescriptionChanging: boolean;
    isLocationChanging: boolean;
    isVirtualLinkChanging: boolean;
  };
}

export interface DashboardListEventQueryResult {
  editableEvents: {
    items: EditableEvent[];
    pages: number;
  };
  companyTags: CompanyTag[];
}

export interface InsightsEventGuestStatsQueryResponse {
  guestStats: GuestStats;
}

export enum T2DomainEnum {
  'T2_NY' = 'T2_NY',
  'ZYNGA' = 'ZYNGA',
  '2K' = '2K',
  'VC' = 'VC'
}

export const T2DomainEnumLabels: Record<T2DomainEnum, string> = {
  [T2DomainEnum.T2_NY]: 'T2',
  [T2DomainEnum.ZYNGA]: 'Zynga',
  [T2DomainEnum['2K']]: '2K',
  [T2DomainEnum.VC]: 'VC'
};

export enum CapacityType {
  UNLIMITED = 'unlimited',
  LIMITED = 'limited'
}

export enum EventFormat {
  VIRTUAL = 'VIRTUAL',
  IN_PERSON = 'IN_PERSON',
  HYBRID = 'HYBRID'
}

export enum EventPublishScheduleStatus {
  SCHEDULED = 'SCHEDULED',
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export interface EventPublishSchedule {
  id: number;
  eventId: number;
  scheduledAt: string;
  timezoneCode?: string | null;
  status: EventPublishScheduleStatus;
  errorMessage?: string | null;
  createdById: number;
  createdAt: string;
  updatedAt: string;
}
